import axios, { AxiosResponse } from 'axios';
import { accessToken } from './agent';
import { creatorApiBaseURL } from './creatorApiConfig';
import { GENERIC_ERROR_MESSAGE } from '../../utils/resolveErrorMessage';
import type { TApiEnvelope, TEmptyApiEnvelope } from '../models/CreatorApiModels';

/**
 * The creator API's shared request plumbing: one axios instance carrying the bearer token, plus
 * envelope unwrapping. The agents that talk to this API — `aiSessionAgent`, `aiMediaAgent`,
 * `coursePageAgent`, `balanceAgent` — build their endpoint groups on the `requests` exported here, the way
 * `synthesiaAgent` builds on the instance `lmsIntegrationAgent` exports. It owns no endpoints of its
 * own, so it has no default export. `createEnvelopeClient` builds the same plumbing for another host
 * that speaks the envelope (`billingApiAgent`).
 *
 * Knows nothing about SignalR, the stores or React. Everything here resolves to unwrapped envelope
 * data or rejects with the envelope's own error message.
 */

/**
 * A refusal the backend wrote itself: its message is the envelope's own words, its status is the one
 * the backend answered with, and its reactName the one the envelope named, if any.
 *
 * Being this type is the promise. Only a failure carrying an envelope becomes one, so a caller may
 * show its message to an author verbatim without checking anything further; a transport that never
 * reached an envelope rejects as whatever it was, and its message is for a log. The status is for an
 * endpoint that answers more than one way — two refusals can both be failures and still mean
 * different things — and is undefined when the envelope did not arrive over HTTP. The reactName tells
 * apart two refusals under one status, such as a 402 for credits and a 402 for debt. The data is
 * whatever the error envelope carried beside its message, null when it carried none: a refusal can say
 * how things stand, such as the balance a 402 was answered with, and the endpoint's agent reads it.
 */
export class CreatorApiError extends Error {
  readonly status: number | undefined;

  readonly reactName: string | undefined;

  readonly data: unknown;

  constructor(message: string, status?: number, reactName?: string, data: unknown = null) {
    super(message);
    this.name = 'CreatorApiError';
    this.status = status;
    this.reactName = reactName;
    this.data = data;
  }
}

const responseBody = <T>(response: AxiosResponse<T>) => response.data;

// A failure envelope can also arrive over HTTP 200, and it is no less the backend's own words for it.
const unwrapEnvelope = <T>(envelope: TApiEnvelope<T>): T | null => {
  if (!envelope || envelope.success !== true) {
    throw new CreatorApiError(envelope?.errorMessage || GENERIC_ERROR_MESSAGE);
  }
  return envelope.data;
};

const unwrapEmptyEnvelope = (envelope: TEmptyApiEnvelope): void => {
  if (!envelope || envelope.success !== true) {
    throw new CreatorApiError(envelope?.errorMessage || GENERIC_ERROR_MESSAGE);
  }
};

/**
 * One envelope-speaking host at `baseURL`: its own axios instance carrying the bearer token, error
 * envelopes arriving as HTTP failures turned into `CreatorApiError`s with their status, and requests
 * that unwrap the envelope. The creator API is one; `CreatorBilling.API` (`billingApiAgent`) is another.
 */
export const createEnvelopeClient = (baseURL: string) => {
  const instance = axios.create({ baseURL });

  instance.interceptors.request.use(function (config) {
    if (accessToken) {
      config.headers.authorization = `Bearer ${accessToken}`;
    } else if (config.headers && 'authorization' in config.headers) {
      delete config.headers.authorization;
    }
    return config;
  });

  // Error envelopes can also arrive as HTTP 400/500; surface their errorMessage.
  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const envelopeErrorMessage = error?.response?.data?.errorMessage;
      if (typeof envelopeErrorMessage !== 'string' || !envelopeErrorMessage) return Promise.reject(error);

      const reactName = error?.response?.data?.reactName;
      return Promise.reject(new CreatorApiError(envelopeErrorMessage, error?.response?.status, typeof reactName === 'string' && reactName ? reactName : undefined, error?.response?.data?.data ?? null));
    },
  );

  return {
    instance,
    requests: {
      getEnvelope: <T>(url: string) => instance.get<TApiEnvelope<T>>(url).then(responseBody).then(unwrapEnvelope),
      postEnvelope: <T>(url: string, body?: unknown) => instance.post<TApiEnvelope<T>>(url, body).then(responseBody).then(unwrapEnvelope),
      postEmptyEnvelope: (url: string, body?: unknown) => instance.post<TEmptyApiEnvelope>(url, body).then(responseBody).then(unwrapEmptyEnvelope),
      putEnvelope: <T>(url: string, body?: unknown) => instance.put<TApiEnvelope<T>>(url, body).then(responseBody).then(unwrapEnvelope),
      deleteEnvelope: <T>(url: string) => instance.delete<TApiEnvelope<T>>(url).then(responseBody).then(unwrapEnvelope),
    },
  };
};

const creatorApi = createEnvelopeClient(creatorApiBaseURL);

export const axiosCreatorApi = creatorApi.instance;

export const requests = creatorApi.requests;

export const requireData = <T>(data: T | null): T => {
  if (data === null || data === undefined) {
    throw new Error(GENERIC_ERROR_MESSAGE);
  }
  return data;
};
